/**
 * @file `SignInCTA` — inline closing CTA for marketing pages.
 *       Replaces the legacy "Be early. Build the muscle" capture
 *       section with the full three-flow entry point.
 * @module @labcraft/ui/chrome/sign-in/sign-in-cta
 *
 * Single horizontal row at desktop — copy on the left, SSO buttons
 * in the middle, email field + submit on the right. Stacks
 * progressively below 960px (copy stacks above; SSO + email on one
 * row) and 600px (everything full-width).
 *
 * Reuses {@link useSignInFlow} so the form lifecycle matches
 * SignInCard / SignInPopover exactly. On successful email submit
 * the entire CTA row swaps in-place to a "Check your inbox" panel
 * (no `onEmailSent` is exposed — the marketing CTA's whole purpose
 * is to keep the user on the landing page).
 *
 * Copy is fully prop-overridable so the manifesto's close and the
 * landing's close can read differently.
 *
 * Renders point-of-collection legal links in a full-width row beneath the
 * sign-in controls (GDPR Art. 13 notice where data is obtained, plus the
 * account Terms): this CTA is the primary sign-up capture, so it links the
 * legal docs where the email / OAuth is handed over. Every consumer is a www
 * surface, so the links are same-origin.
 */

import type { FormEvent, JSX } from 'react'
import { GithubLogo } from './github_logo.tsx'
import { GoogleLogo } from './google_logo.tsx'
import { useSignInFlow, type UseSignInFlowProps } from './use_sign_in_flow.ts'
import styles from './sign_in_cta.module.css'

/**
 * Props for {@link SignInCTA}.
 *
 * Extends {@link UseSignInFlowProps} except `onEmailSent` is dropped
 * — the CTA always swaps in-place to its "Check your inbox" panel
 * on submit (the whole point of this surface is to keep the user
 * on the landing page).
 *
 * @property title - Heading. Default `"Start your first lab."`
 * @property lede - One-line subhead under the title.
 * @property emailTtlLabel - Label inside the post-submit panel. Default `"15 minutes"`.
 */
export interface SignInCTAProps extends Omit<
  UseSignInFlowProps,
  'onEmailSent'
> {
  title?: string
  lede?: string
  emailTtlLabel?: string
}

const DEFAULT_TITLE = 'Start your first lab.'
const DEFAULT_LEDE =
  'GitHub, Google, or a magic link to your inbox — pick your way in.'

export function SignInCTA(props: SignInCTAProps): JSX.Element {
  const title = props.title ?? DEFAULT_TITLE
  const lede = props.lede ?? DEFAULT_LEDE
  const emailTtlLabel = props.emailTtlLabel ?? '15 minutes'

  const flow = useSignInFlow(props)

  function onSubmitForm(e: FormEvent): void {
    void flow.onSubmit(e)
  }

  if (flow.mode === 'sent') {
    return (
      <section className={styles.frame}>
        <div
          className={`${styles.row} ${styles.rowSent}`}
          role="status"
          aria-live="polite">
          <div className={styles.sentCopy}>
            <p className={styles.eyebrow}>Check your inbox</p>
            <h2 className={styles.sentTitle}>One link, on its way.</h2>
            <p className={styles.sentBody}>
              We sent a link to <strong>{flow.sentEmail}</strong>. Click it to
              sign in. The link is good for {emailTtlLabel}.
            </p>
            <p className={styles.fineprint}>
              Didn&apos;t arrive?{' '}
              <button
                type="button"
                className={styles.linkButton}
                onClick={flow.reset}>
                Send a new one
              </button>
            </p>
          </div>
        </div>
      </section>
    )
  }

  const submitting = flow.mode === 'submitting'

  return (
    <section className={styles.frame}>
      <div className={styles.row}>
        <div className={styles.copy}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.lede}>{lede}</p>
        </div>

        <div className={styles.controls}>
          {flow.errorMessage && (
            <div className={styles.errorBanner} role="alert">
              {flow.errorMessage}
            </div>
          )}

          <div className={styles.ssoRow}>
            <button
              type="button"
              className={styles.ssoButton}
              onClick={flow.onGithub}
              disabled={submitting}
              aria-label="Continue with GitHub">
              <GithubLogo className={styles.ssoLogo} />
              GitHub
            </button>
            <button
              type="button"
              className={styles.ssoButton}
              onClick={flow.onGoogle}
              disabled={submitting}
              aria-label="Continue with Google">
              <GoogleLogo className={styles.ssoLogo} />
              Google
            </button>
          </div>

          <form className={styles.emailRow} onSubmit={onSubmitForm} noValidate>
            <input
              className={styles.input}
              type="email"
              placeholder="you@yourcompany.com"
              value={flow.email}
              onChange={e => flow.setEmail(e.target.value)}
              autoComplete="email"
              required
              aria-label="Email"
            />
            <button
              type="submit"
              className={styles.submit}
              disabled={!flow.canSubmit}>
              {submitting ? 'Sending…' : 'Email link →'}
            </button>
          </form>
        </div>

        <p className={styles.legalNote}>
          By continuing, you confirm that you are at least 18, agree to our{' '}
          <a href="/terms">Terms</a>, and acknowledge our{' '}
          <a href="/privacy">Privacy Policy</a>.
        </p>
      </div>
    </section>
  )
}
